// src/Pages/UserDetailsPage.jsx
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Button from "../components/Button";
import FloatingLabelInput from "../components/FloatingLabelInput";
import { Box, Container, Grid, Paper, Typography, Checkbox, Avatar, Divider, Snackbar, useTheme, useMediaQuery, CircularProgress, Tooltip} from "@mui/material";
import { Person as PersonIcon, Phone as PhoneIcon, Email as EmailIcon, LocationOn as LocationOnIcon} from "@mui/icons-material";
import { AiOutlinePlus, AiOutlineSearch, AiOutlineReload } from "react-icons/ai";
import Chip from '@mui/material/Chip';

// Define 4 color constants
const avatarColors = [
  "#004225", // British Racing Green
  "#800020", // Burgundy Red
  "#006D77", // Deep Teal (Replaces Slate 700)
  "#FF6F00", // Surprise contrasting color (Amber/Deep Orange)
];

const API_BASE = "http://192.168.1.248:3103";
const PAGE_LIMIT = 10;

export default function UserDetailsPage({ loggedInUser }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const companyId = loggedInUser?.companyId;

  const [searchData, setSearchData] = useState({ userName: "", employeeName: "", mobileNumber: "", emailId: "" });
  const [users, setUsers] = useState([]);
  const [offset, setOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [notification, setNotification] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchUsers = useCallback(async (reset = false, filters = searchData) => {
    if (!companyId) return;
    setLoading(true);
    try {
      const currentOffset = reset ? 0 : offset;
      const { data: res } = await axios.get(`${API_BASE}/api/userssearch`, {
        params: { companyId, offset: currentOffset, limit: PAGE_LIMIT, ...filters }
      });
      if (res.success) {
        const fetched = res.users || [];
        setUsers(prev => reset ? fetched : [...prev, ...fetched]);
        setOffset(prev => reset ? fetched.length : prev + fetched.length);
        setTotalCount(res.totalCount);
      }
    } catch (err) {
      console.error("Fetch error:", err);
      setNotification("Error loading users");
    } finally {
      setLoading(false);
    }
  }, [companyId, offset, searchData]);

  useEffect(() => {
    fetchUsers(true);
  }, [fetchUsers]);

  const handleSearchChange = e => {
    const { name, value } = e.target;
    setSearchData(prev => ({ ...prev, [name]: value }));
  };
  const handleSearch = () => fetchUsers(true);
  const handleReset = () => {
    const empty = { userName: "", employeeName: "", mobileNumber: "", emailId: "" };
    setSearchData(empty);
    fetchUsers(true, empty);
  };
  const handleShowMore = () => {
    if (users.length < totalCount) fetchUsers(false);
  };
  const goToEdit = userId => navigate("/main/usermaster", { state: { mode: "edit", userId } });
  const addNewUser = () => navigate("/main/usermaster", { state: { mode: "insert" } });
  const toggleSelectOne = id => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleSelectAll = () => setSelectedIds(selectedIds.length === users.length ? [] : users.map(u => u.userId));
  
  const handleDelete = async () => {
    if (!selectedIds.length) {
      setNotification("No users selected");
      return;
    }
    try {
      const chk = await axios.post(`${API_BASE}/api/check-inquiry`, { userIds: selectedIds });
      if (chk.status === 400) {
        setNotification("Cannot delete: inquiries exist");
        return;
      }
      const del = await axios.post(`${API_BASE}/api/delete-users`, { userIds: selectedIds });
      if (del.data.success) {
        setUsers(prev => prev.filter(u => !selectedIds.includes(u.userId)));
        setSelectedIds([]);
        setNotification("Users deleted successfully");
      } else {
        setNotification(del.data.message || "Delete failed");
      }
    } catch (err) {
      console.error("Delete error:", err);
      setNotification("Server error while deleting");
    }
  };

  return (
    <Container sx={{ py: 4 }}>
      <Typography variant="h4" align="center" sx={{ mb: 3, textDecoration: "underline" }}>
        User Details
      </Typography>

      <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>Search Section</Typography>
        <Grid container spacing={2}>
          {[
            ["userName", "User Name"],
            ["employeeName", "Employee Name"],
            ["mobileNumber", "Mobile Number"],
            ["emailId", "Email ID"]
          ].map(([field, label]) => (
            <Grid item xs={12} md={3} key={field}>
              <FloatingLabelInput label={label} name={field} value={searchData[field]} onChange={handleSearchChange} fullWidth />
            </Grid>
          ))}
        </Grid>
        <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
          <Button startIcon={<AiOutlineSearch />} onClick={handleSearch}  sx={{ bgcolor: "#1877F2", color: "#fff",   }}>Search</Button>
          <Button startIcon={<AiOutlineReload />} color="error" onClick={handleReset}>Reset</Button>
        </Box>
      </Paper>

      <Box display="flex" flexDirection={isMobile ? "column" : "row"} alignItems="center" justifyContent="space-between" mb={4} gap={2}>
        <Button startIcon={<AiOutlinePlus />} onClick={addNewUser} sx={{ bgcolor: "#1877F2", color: "#fff",   }}>Add</Button>
        <Typography>Count: {users.length} / {totalCount}</Typography>
        <Box display="flex" gap={2}>
          {users.length < totalCount && <Button onClick={handleShowMore} disabled={loading}>{loading ? "Loading…" : "Show More"}</Button>}
          <Button color="error" onClick={handleDelete}>Delete</Button>
        </Box>
      </Box>

      {loading && offset === 0 ? (
        <Box textAlign="center" py={4}><CircularProgress /></Box>
      ) : users.length ? (
        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <Checkbox checked={selectedIds.length === users.length} indeterminate={selectedIds.length > 0 && selectedIds.length < users.length} onChange={toggleSelectAll} size="small" />
            <Typography>Select All</Typography>
          </Box>
          {users.map((u, idx) => (
            <Paper key={u.userId} elevation={0}
              sx={{
                p: 1, borderRadius: 4,
                bgcolor: "#f9fbfc", border: "1px solid #e0e0e0",
                transition: "box-shadow 0.2s ease-in-out",
                "&:hover": { boxShadow: "0 2px 12px rgba(0,0,0,0.08)", bgcolor: "#ffffff" },
                cursor: "pointer"
              }}
              onClick={() => goToEdit(u.userId)}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
                <Checkbox checked={selectedIds.includes(u.userId)} onClick={e => e.stopPropagation()} onChange={() => toggleSelectOne(u.userId)} size="small" />

                <Avatar sx={{ bgcolor: avatarColors[idx % avatarColors.length], color: "white", fontSize: 18 }}>
                  {u.userName?.charAt(0).toUpperCase()}
                </Avatar>

                <Typography variant="h6" sx={{ flexGrow: 1, cursor: "pointer", fontWeight: 500 }} onClick={() => goToEdit(u.userId)} >
                  {u.userName}
                </Typography>
              </Box>

              <Divider sx={{ mb: 1 }} />

              <Grid container spacing={1}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Tooltip title="Employee Name">
                      <PersonIcon sx={{ fontSize: 18, color: "#616161" }} />
                    </Tooltip>
                    <Typography variant="body2">{u.employeeName || "—"}</Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Tooltip title="Mobile Number">
                      <PhoneIcon sx={{ fontSize: 18, color: "#616161" }} />
                    </Tooltip>
                    <Typography variant="body2">{u.mobileNumber || "—"}</Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Tooltip title="Email ID">
                      <EmailIcon sx={{ fontSize: 18, color: "#616161" }} />
                    </Tooltip>
                    <Typography variant="body2">{u.emailId || "—"}</Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Tooltip title="User Category">
                      <Chip label={u.userCategory || "—"} size="small" />
                    </Tooltip>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          ))}
        </Box>
      ) : (
        <Typography align="center" color="textSecondary" py={4}>No records found.</Typography>
      )}

      <Snackbar open={!!notification} message={notification} onClose={() => setNotification("")} autoHideDuration={3000} />
    </Container>
  );
}